import { EditDiscussionMessageState } from '@front-app/components/discussion/Model';
import {
	DiscussionMessage,
} from '@front-app/services/GroupsApi/DiscussionModel';
import {
	editGroupDiscussionMessage,
	editGroupDiscussionCategories,
	GroupDiscussionEditAction,
} from '@front-app/services/GroupsApi/GroupsDiscussionApi';
import { isNullish } from '@front-app/util/types';
import { defineStore } from 'pinia';

export interface MessageStoreState {
	messages: DiscussionMessage[];
}

export const useDiscussionMessageStore = defineStore('discussionMessageStore', {
	state: (): MessageStoreState => ({
		messages: [],
	}),
	getters: {
		getById() {
			return (messageId: string): DiscussionMessage | undefined => this.messages.find(({ id }) => id === messageId);
		},
		getAttachmentParentId() {
			return (attachmentId: string): string | undefined => this.messages
				.find(({ attachments }) => attachments.contents.find(({ id }) => id === attachmentId))
				?.id;
		},
	},
	actions: {
		set(newMessages: DiscussionMessage[]) {
			const messageIds = newMessages.map(({ id }) => id);
			this.$patch((state) => {
				const oldMessages = state.messages.filter(({ id }) => !messageIds.includes(id));
				state.messages = oldMessages.concat(newMessages);
			});
		},
		async remove(messageId: string) {
			const message = this.getById(messageId);
			if (!message) {
				return Promise.reject();
			}
			const result = await editGroupDiscussionMessage(message, GroupDiscussionEditAction.REMOVE);
			message.removedAt = result.removedAt;
			message.removedBySender = result.removedBySender;
			this.set([message]);
			return Promise.resolve();
		},
		async restore(messageId: string) {
			const message = this.getById(messageId);
			if (!message) {
				return Promise.reject();
			}
			const result = await editGroupDiscussionMessage(message, GroupDiscussionEditAction.SHOW);
			message.removedAt = result.removedAt;
			message.removedBySender = result.removedBySender;
			this.set([message]);
			return Promise.resolve();
		},
		async edit(messageId: string, payload: Partial<EditDiscussionMessageState>) {
			if (!messageId) {
				return Promise.reject();
			}
			const messageStore = useDiscussionMessageStore();
			const message = messageStore.getById(messageId);
			if (!message) {
				return Promise.reject();
			}
			if (!isNullish(payload.subjectText)) {
				const response = await editGroupDiscussionMessage({
					...message,
					subject: payload.subjectText,
				}, GroupDiscussionEditAction.SUBJECT);
				message.subject = response.subject;
			}
			if (!isNullish(payload.categories)) {
				const response = await editGroupDiscussionCategories({
					...message,
					categories: payload.categories,
				});
				message.categories = response.categories;
			}
			if (!isNullish(payload.messageText)) {
				const response = await editGroupDiscussionMessage({
					...message,
					messageText: payload.messageText,
				}, GroupDiscussionEditAction.MESSAGE);
				message.messageText = response.messageText;
			}
			messageStore.set([message]);
			return message;
		},
	},
});
